/**
 * Step 9.H/9.I — Payout Run. Finance approves and pays one period from a single screen:
 * the economics waterfall, the confirmation card, the blockers and live money movement.
 * Every number is read from the ledgers; nothing is recomputed in the browser.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, BadgeCheck, Banknote, CheckCircle2, Clock, Lock, RefreshCw,
  ShieldAlert, TrendingDown, TrendingUp, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { errorText, useLatestRequest } from '../components/finance/financeUi';
import { downloadStatement } from '../components/finance/PayoutStatementsCard';

const money = (value, dash = '—') =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
    : dash;

const compact = (value) =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', notation: 'compact',
                                      maximumFractionDigits: 1 })
    : '—';

const currentPeriod = () => new Date().toISOString().slice(0, 7);

const STATUS_TONE = {
  SETTLED: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  PROCESSING: 'bg-sky-500/10 text-sky-700 border-sky-500/30',
  SUBMITTED: 'bg-sky-500/10 text-sky-700 border-sky-500/30',
  READY_FOR_PAYMENT: 'bg-indigo-500/10 text-indigo-700 border-indigo-500/30',
  APPROVED: 'bg-indigo-500/10 text-indigo-700 border-indigo-500/30',
  UNDER_REVIEW: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  DRAFT: 'bg-muted text-muted-foreground border-transparent',
  PAID: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  RETURNED: 'bg-rose-500/10 text-rose-700 border-rose-500/30',
  FAILED: 'bg-rose-500/10 text-rose-700 border-rose-500/30',
  RECONCILIATION_REQUIRED: 'bg-orange-500/10 text-orange-700 border-orange-500/30',
  CANCELLED: 'bg-muted text-muted-foreground border-transparent',
};

const SEVERITY_TONE = {
  critical: 'bg-rose-500/10 text-rose-700',
  high: 'bg-rose-500/10 text-rose-700',
  medium: 'bg-amber-500/10 text-amber-700',
  low: 'bg-muted text-muted-foreground',
};

const Pill = ({ value }) => (
  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-wide
                    ${STATUS_TONE[value] || 'bg-muted text-muted-foreground border-transparent'}`}>
    {String(value || '').replace(/_/g, ' ')}
  </span>
);

const Change = ({ value }) => {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="h-3.5 w-3.5" />{up ? '+' : ''}{value}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, change, tone = '', testid, children }) => (
  <div className="rounded-xl border bg-card p-5 transition-shadow hover:shadow-sm" data-testid={testid}>
    <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
      <Icon className="h-4 w-4" />{label}
    </p>
    {children || <p className={`mt-2 text-2xl font-semibold ${tone}`}>{value}</p>}
    {change != null && <div className="mt-1"><Change value={change} /></div>}
  </div>
);

export default function PayoutRunPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'super_admin';
  const canRunPayouts = ['super_admin', 'iso'].includes(user?.role);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const scope = useMemo(
    () => (isSuperAdmin && tenantId ? `&tenant_id=${tenantId}` : ''), [isSuperAdmin, tenantId],
  );

  const nextRequest = useLatestRequest();
  const load = useCallback(async () => {
    if (isSuperAdmin && !tenantId) { setData(null); return; }
    const isCurrent = nextRequest();
    setLoading(true);
    setError('');
    try {
      const payload = await api.request(
        `/financials/payout-run?period=${encodeURIComponent(period)}${scope}`);
      if (isCurrent()) setData(payload);
    } catch (err) {
      if (isCurrent()) { setData(null); setError(errorText(err, 'Could not load the payout run')); }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [period, scope, isSuperAdmin, tenantId, nextRequest]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.request('/super-admin/tenants')
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.tenants || []);
        setTenants(rows);
        setTenantId((current) => current || rows[0]?.id || rows[0]?._id || '');
      })
      .catch(() => setTenants([]));
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && !tenantId) return undefined;
    let alive = true;
    api.request(`/financials/disbursement-accounts?${scope.slice(1) || ''}`)
      .then((list) => { if (alive) { setAccounts(list || []); setAccount((list || [])[0]?.provider_account_id || ''); } })
      .catch(() => alive && setAccounts([]));
    return () => { alive = false; };
  }, [scope, isSuperAdmin, tenantId]);

  const [delivery, setDelivery] = useState('');
  useEffect(() => {
    if (isSuperAdmin && !tenantId) return undefined;
    let alive = true;
    api.request(`/financials/settings?${scope.slice(1)}`)
      .then((settings) => alive && setDelivery(settings.statement_delivery)).catch(() => alive && setDelivery(''));
    return () => { alive = false; };
  }, [scope, isSuperAdmin, tenantId]);
  const saveDelivery = async (value) => {
    try {
      const settings = await api.request('/financials/settings', { method: 'PUT', body: JSON.stringify({
        statement_delivery: value, ...(isSuperAdmin && tenantId ? { tenant_id: tenantId } : {}) }) });
      setDelivery(settings.statement_delivery);
      toast.success('Statement delivery preference saved — sending starts with the Communication Engine');
    } catch (err) { toast.error(errorText(err)); }
  };

  const kpis = data?.kpis;
  const checks = data?.pre_approval_checks || [];
  const issues = useMemo(() => {
    const seen = new Set((data?.blockers || []).map((b) => b.type));
    return [
      ...(data?.blockers || []).map((b) => ({ ...b, blocking: true })),
      ...(data?.exceptions || []).filter((e) => !seen.has(e.type)),
    ];
  }, [data]);
  const blockingIssues = useMemo(
    () => (data?.blockers || []).reduce((sum, b) => sum + b.count, 0), [data],
  );
  const badge = useMemo(() => {
    if (data?.fully_paid) return { label: 'Paid', tone: 'bg-emerald-500/10 text-emerald-700' };
    if (!data?.payout_eligible) return { label: 'Blocked', tone: 'bg-amber-500/10 text-amber-700' };
    if (data?.kpis?.disbursements?.processing) {
      return { label: 'Paying', tone: 'bg-sky-500/10 text-sky-700' };
    }
    if (!data?.actions?.approvable && data?.actions?.creditable) {
      return { label: 'Approved', tone: 'bg-indigo-500/10 text-indigo-700' };
    }
    return { label: 'Ready for review', tone: 'bg-emerald-500/10 text-emerald-700' };
  }, [data]);
  const [confirming, setConfirming] = useState('');

  const run = async (what) => {
    setConfirming('');
    setBusy(what);
    try {
      if (what === 'approve') {
        const result = await api.request('/financials/payout-run/approve', {
          method: 'POST', body: JSON.stringify({ period, ...(isSuperAdmin && tenantId ? { tenant_id: tenantId } : {}) }),
        });
        toast.success(`${result.approved.length} payout(s) approved — ${money(result.approved_total)} locked`);
        if (result.held_for_bank_verification?.length) {
          toast.warning(`${result.held_for_bank_verification.length} approved but held — bank not verified`);
        }
        if (result.failed?.length) toast.error(`${result.failed.length} could not be approved`);
      } else if (what === 'release') {
        const result = await api.request('/financials/payout-run/release-held', {
          method: 'POST', body: JSON.stringify({ period, ...(isSuperAdmin && tenantId ? { tenant_id: tenantId } : {}) }),
        });
        toast.success(`${result.released.length} payout(s) released for payment`);
      } else {
        if (!account) { toast.error('Pick an ACH provider account first'); return; }
        const result = await api.request('/financials/payout-run/credit', {
          method: 'POST', body: JSON.stringify({ period, provider_account_id: account,
                                 ...(isSuperAdmin && tenantId ? { tenant_id: tenantId } : {}) }),
        });
        toast.success(`${result.selected} selected · ${result.sent.length} ACH credit(s) submitted — ${money(result.sent_total)}`);
        if (result.blocked_by_bank_verification) {
          toast.warning(`${result.blocked_by_bank_verification} excluded — blocked by bank verification`);
        }
        if (result.refused?.length) toast.error(`${result.refused.length} refused (duplicate or not ready)`);
      }
      await load();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const rowAction = async (disbursementId, action) => {
    setBusy(disbursementId);
    try {
      const result = await api.request(`/financials/disbursements/${disbursementId}/${action}`,
                                       { method: 'POST' });
      toast.success(`Now ${String(result.status).replace(/_/g, ' ').toLowerCase()}`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'The provider could not be reached');
    } finally {
      setBusy('');
    }
  };

  if (!canRunPayouts) {
    return (
      <div className="mx-auto max-w-xl rounded-xl border bg-card p-10 text-center"
           data-testid="payout-run-denied">
        <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-semibold">Finance only</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Payout runs are approved and paid by an ISO owner or the platform finance team. Your
          own residual economics live on the Payer Economics page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="payout-run-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Payout Run</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Approve the period, then pay it. Amounts lock at approval and every credit is
            idempotent — nobody can pay the same obligation twice.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {isSuperAdmin && (
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Tenant</span>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                      data-testid="payout-run-tenant-select">
                {!tenants.length && <option value="">No tenants</option>}
                {tenants.map((t) => (
                  <option key={t.id || t._id} value={t.id || t._id}>
                    {t.company_name || t.name || t.subdomain}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Residual period</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                   className="h-10 rounded-md border bg-background px-3 text-sm"
                   data-testid="payout-run-period-input" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Statement delivery</span>
            <select value={delivery} onChange={(e) => saveDelivery(e.target.value)}
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    data-testid="statement-delivery-select">
              <option value="PORTAL_LINK">Portal link</option>
              <option value="PDF_ATTACHMENT">PDF attachment</option>
            </select>
          </label>
          <button type="button" onClick={load} data-testid="payout-run-refresh-btn"
                  className="h-10 inline-flex items-center gap-2 rounded-full border px-4 text-sm transition-colors hover:bg-muted">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-600"
             data-testid="payout-run-error">{error}</div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={Banknote} label="Actual revenue made" value={money(kpis?.actual_revenue)}
             change={kpis?.actual_revenue_change_pct} testid="kpi-actual-revenue" />
        <Kpi icon={Wallet} label="Downline to be paid" value={money(kpis?.downline_payable)}
             change={kpis?.downline_payable_change_pct} testid="kpi-downline-payable" />
        <Kpi icon={TrendingUp} label="Profit to the house" value={money(kpis?.house_profit)}
             change={kpis?.house_profit_change_pct} testid="kpi-house-profit" />
        <Kpi icon={BadgeCheck} label="Pending adjustments" value={money(kpis?.pending_adjustments)}
             tone="text-amber-600" testid="kpi-pending-adjustments" />
        <Kpi icon={ShieldAlert} label="Blocked payouts" value={kpis?.blocked_payouts ?? '—'}
             tone={kpis?.blocked_payouts ? 'text-rose-600' : 'text-emerald-600'}
             testid="kpi-blocked-payouts" />
        <Kpi icon={Clock} label="Disbursement status" testid="kpi-disbursement-status">
          <div className="mt-2 flex items-end gap-4">
            {[['processing', 'Processing', 'text-sky-600'], ['settled', 'Settled', 'text-emerald-600'],
              ['returned', 'Returned', 'text-rose-600']].map(([key, label, tone]) => (
              <div key={key}>
                <p className={`text-xl font-semibold ${tone}`}>{kpis?.disbursements?.[key] ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </Kpi>
      </section>

      <section className="grid gap-6 xl:grid-cols-4">
        <div className="min-w-0 rounded-xl border bg-card xl:col-span-2" data-testid="payout-run-waterfall">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="text-base md:text-lg font-medium">Payout run summary</h2>
            <span className="text-xs text-muted-foreground">
              vs {data?.prior_period || 'prior period'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Share</th>
                  <th className="px-5 py-3 font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {(data?.waterfall || []).map((line) => (
                  <tr key={line.key} className={`border-t transition-colors hover:bg-muted/40
                                                 ${line.key === 'house_profit' ? 'font-medium' : ''}
                                                 ${line.informational ? 'text-muted-foreground' : ''}`}
                      data-testid={`waterfall-${line.key}`}>
                    <td className="px-5 py-3">{line.label}</td>
                    <td className={`px-5 py-3 tabular-nums ${line.amount < 0 ? 'text-rose-600' : ''}`}>
                      {money(line.amount)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 rounded-full bg-muted">
                          <div className="h-1.5 rounded-full bg-primary transition-[width] duration-500"
                               style={{ width: `${Math.min(100, Math.abs(line.pct_of_gross || 0))}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {line.pct_of_gross != null ? `${line.pct_of_gross}%` : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3"><Change value={line.change_pct} /></td>
                  </tr>
                ))}
                {!loading && !(data?.waterfall || []).some((l) => l.amount) && (
                  <tr><td colSpan={4} className="px-5 py-10 text-center text-muted-foreground"
                          data-testid="payout-run-empty">
                    Nothing calculated for {period} yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="min-w-0 rounded-xl border bg-card" data-testid="approve-and-pay-card">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="text-base md:text-lg font-medium">Approve &amp; pay</h2>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.tone}`}
                  data-testid="approve-eligibility-badge">{badge.label}</span>
          </div>
          <dl className="space-y-2 px-5 py-4 text-sm">
            {[
              ['Batch ID', data?.batch?.batch_id || '—'],
              ['Period', period],
              ['Payees', kpis?.payees ?? 0],
              ['Awaiting approval', data?.actions?.approvable ?? 0],
              ['Ready to credit', data?.actions?.creditable ?? 0],
              ['Blocked by bank verification', data?.actions?.blocked_by_bank_verification ?? 0],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium tabular-nums">{value}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 pt-1">
              <dt className="text-muted-foreground">Total to disburse</dt>
              <dd className="text-2xl font-semibold" data-testid="total-to-disburse">
                {money(kpis?.to_disburse)}
              </dd>
            </div>
          </dl>

          <div className="border-t px-5 py-4">
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">
              ACH provider
            </label>
            <select value={account} onChange={(e) => setAccount(e.target.value)}
                    className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="ach-provider-select">
              {!accounts.length && <option value="">No ACH-capable provider connected</option>}
              {accounts.map((a) => (
                <option key={a.provider_account_id} value={a.provider_account_id}>
                  {a.name}{a.adapter_status ? ` · ${a.adapter_status}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t px-5 py-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
              Pre-approval checks
            </p>
            <ul className="space-y-2 text-sm" data-testid="pre-approval-checks">
              {checks.map((check) => (
                <li key={check.key} className="flex items-center gap-2"
                    data-testid={`check-${check.key}`}>
                  {check.ok
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <span className={check.ok ? 'text-muted-foreground' : ''}>
                    {check.count} {check.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t px-5 py-4 space-y-3">
            {confirming && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm"
                   data-testid="run-confirm-panel">
                <p className="font-medium">
                  {confirming === 'approve'
                    ? `Lock ${data?.actions?.approvable ?? 0} payout(s) at ${money(kpis?.to_disburse)}?`
                    : `Send ${data?.actions?.creditable ?? 0} ACH credit(s)?`}
                </p>
                {confirming === 'credit' && (
                  <p className="mt-1 text-xs font-medium" data-testid="credit-selection-summary">
                    {data?.actions?.selected ?? 0} selected · {data?.actions?.creditable ?? 0} ready ·{' '}
                    <span className="text-rose-600">{data?.actions?.blocked_by_bank_verification ?? 0} blocked by bank verification</span>
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {confirming === 'approve'
                    ? 'Approved amounts can only change through a reasoned adjustment.'
                    : 'This creates real payment instructions on the selected rail.'}
                </p>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => run(confirming)}
                          data-testid="run-confirm-yes"
                          className="h-9 flex-1 rounded-full bg-primary px-3 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90">
                    Yes, {confirming === 'approve' ? 'approve' : 'send'}
                  </button>
                  <button type="button" onClick={() => setConfirming('')}
                          data-testid="run-confirm-cancel"
                          className="h-9 flex-1 rounded-full border px-3 text-xs transition-colors hover:bg-muted">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <button type="button" onClick={() => setConfirming('approve')}
                    disabled={!data?.actions?.can_approve || busy === 'approve'}
                    data-testid="approve-payouts-btn"
                    className="h-11 w-full rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground
                               transition-opacity hover:opacity-90 disabled:opacity-40">
              {busy === 'approve' ? 'Approving…' : `Approve ${data?.actions?.approvable ?? 0} payout(s)`}
            </button>
            <button type="button" onClick={() => setConfirming('credit')}
                    disabled={!data?.actions?.can_credit || !account || busy === 'credit'}
                    data-testid="credit-ach-btn"
                    className="h-11 w-full rounded-full border px-4 text-sm font-medium
                               transition-colors hover:bg-muted disabled:opacity-40">
              {busy === 'credit' ? 'Submitting…' : `Credit ACH (${data?.actions?.creditable ?? 0})`}
            </button>
            {data?.actions?.releasable > 0 && (
              <button type="button" onClick={() => run('release')} disabled={busy === 'release'}
                      data-testid="release-held-btn"
                      className="h-10 w-full rounded-full border border-emerald-500/40 px-4 text-sm text-emerald-700 transition-colors hover:bg-emerald-500/10 disabled:opacity-40">
                Release {data.actions.releasable} now-verified payout(s)
              </button>
            )}
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Amounts lock at approval. Crediting creates one ACH credit per obligation; a repeat
              click can never send a second one.
            </p>
          </div>
        </div>

        <div className="min-w-0 rounded-xl border bg-card" data-testid="blockers-card">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="text-base md:text-lg font-medium">Blockers &amp; exceptions</h2>
            <span className="text-xs text-muted-foreground">{blockingIssues} blocking</span>
          </div>
          <ul className="divide-y">
            {issues.map((item) => (
              <li key={item.type} className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  data-testid={`exception-${item.type}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.owner}{item.blocking ? ' · blocks approval' : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">{item.count}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] uppercase ${SEVERITY_TONE[item.severity] || ''}`}>
                    {item.severity}
                  </span>
                </div>
              </li>
            ))}
            {!issues.length && (
              <li className="px-5 py-10 text-center text-sm text-muted-foreground"
                  data-testid="no-exceptions">Nothing is blocking this period.</li>
            )}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="min-w-0 rounded-xl border bg-card" data-testid="live-disbursements">
          <div className="border-b px-5 py-4">
            <h2 className="text-base md:text-lg font-medium">Live disbursement status</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {['Payee', 'Amount', 'Status', 'Updated', ''].map((h) => (
                    <th key={h} className="px-5 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.disbursement_rows || []).map((row) => (
                  <tr key={row.disbursement_id} className="border-t transition-colors hover:bg-muted/40"
                      data-testid={`disbursement-row-${row.disbursement_id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium">{row.payee_name}</p>
                      <p className="text-xs text-muted-foreground">{row.role || 'ACH'}</p>
                    </td>
                    <td className="px-5 py-3 tabular-nums">{money(row.amount)}</td>
                    <td className="px-5 py-3"><Pill value={row.status} /></td>
                    <td className="px-5 py-3 text-xs text-muted-foreground">
                      {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.status === 'RECONCILIATION_REQUIRED' ? (
                        <button type="button" onClick={() => rowAction(row.disbursement_id, 'reconcile')}
                                disabled={busy === row.disbursement_id}
                                data-testid={`reconcile-btn-${row.disbursement_id}`}
                                className="rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-40">
                          Reconcile
                        </button>
                      ) : ['SUBMITTED', 'PROCESSING'].includes(row.status) ? (
                        <button type="button" onClick={() => rowAction(row.disbursement_id, 'refresh')}
                                disabled={busy === row.disbursement_id}
                                data-testid={`refresh-btn-${row.disbursement_id}`}
                                className="rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-40">
                          Check status
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {row.return_code || row.failure_reason ? 'See exception' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {!(data?.disbursement_rows || []).length && (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground"
                          data-testid="no-disbursements">
                    No money has moved for {period} yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="min-w-0 rounded-xl border bg-card" data-testid="payout-entries">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="text-base md:text-lg font-medium">Payouts in this run</h2>
            <span className="text-xs text-muted-foreground">
              {Object.entries(data?.entry_status_counts || {})
                .map(([s, c]) => `${c} ${s.replace(/_/g, ' ').toLowerCase()}`).join(' · ') || '—'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {['Payee', 'Volume', 'Tier', 'Payout', 'Status', ''].map((h) => (
                    <th key={h} className="px-5 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.entries || []).map((row) => (
                  <tr key={row.entry_id} className="border-t transition-colors hover:bg-muted/40"
                      data-testid={`payout-entry-${row.entry_id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium">{row.payee_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.role || '—'}{row.bank_verified ? ` · •••• ${row.bank_last4}` : ''}
                      </p>
                    </td>
                    <td className="px-5 py-3 tabular-nums">{compact(row.volume)}</td>
                    <td className="px-5 py-3">
                      {row.split_percent != null ? `${row.split_percent}%` : '—'}
                      {row.tier ? <span className="ml-1 text-xs text-muted-foreground">{row.tier}</span> : null}
                    </td>
                    <td className="px-5 py-3 font-medium tabular-nums">
                      {money(row.approved_amount ?? row.amount)}
                      {row.adjustments_total ? (
                        <span className="ml-2 text-xs text-amber-600">
                          {row.adjustments_total > 0 ? '+' : ''}{money(row.adjustments_total)} adj
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3">
                      <Pill value={row.status} />
                      {!row.bank_verified && !['PAID', 'CANCELLED'].includes(row.status) && (
                        <p className="mt-1 text-[11px] text-rose-600" data-testid={`payment-hold-${row.entry_id}`}>
                          {String(row.payment_hold || row.payment_profile_status).replace(/_/g, ' ').toLowerCase()}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {['APPROVED', 'READY_FOR_PAYMENT', 'PAID'].includes(row.status) && (
                        <button type="button" onClick={() => downloadStatement({ entry_id: row.entry_id, period })}
                                data-testid={`statement-btn-${row.entry_id}`}
                                className="rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted">
                          Statement
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!(data?.entries || []).length && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground"
                          data-testid="no-entries">
                    No payout entries built for {period} yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
